import { useState, useEffect, useCallback, useMemo } from "react";
import { formatDate } from "../utils/formatDate";

/**
 * Enhanced search hook with debouncing to prevent excessive re-renders
 */
export const useDataGridSearchDebounced = (
  data,
  searchFieldsOrSetPagination = null,
  delay = 300
) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  // Determine if second parameter is searchFields array or setPaginationModel function
  const isSearchFields = Array.isArray(searchFieldsOrSetPagination);
  const searchFields = isSearchFields ? searchFieldsOrSetPagination : null;
  const setPaginationModel = isSearchFields
    ? null
    : searchFieldsOrSetPagination;

  // Debounce the search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      // Reset pagination when search changes
      if (searchValue !== debouncedSearchValue && setPaginationModel) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [searchValue, delay, debouncedSearchValue, setPaginationModel]);

  // Stable search change handler
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // Filter data based on debounced search value
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    if (!debouncedSearchValue) return data;

    const searchTerm = debouncedSearchValue.toLowerCase();
    return data.filter((row) => {
      // If specific search fields are provided, only search those fields
      if (searchFields && searchFields.length > 0) {
        return searchFields.some((field) => {
          const value = row[field];
          if (value === null || value === undefined) return false;

          // Handle Firestore Timestamp objects
          if (typeof value === "object" && value.toDate) {
            return formatDate(value).toLowerCase().includes(searchTerm);
          }

          // Handle Date objects
          if (value instanceof Date) {
            return formatDate(value).toLowerCase().includes(searchTerm);
          }

          // Handle regular values
          return value.toString().toLowerCase().includes(searchTerm);
        });
      }

      // Otherwise, search all fields (original behavior)
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;

        // Handle Firestore Timestamp objects
        if (typeof value === "object" && value.toDate) {
          return formatDate(value).toLowerCase().includes(searchTerm);
        }

        // Handle Date objects
        if (value instanceof Date) {
          return formatDate(value).toLowerCase().includes(searchTerm);
        }

        // Handle regular values
        return value.toString().toLowerCase().includes(searchTerm);
      });
    });
  }, [data, debouncedSearchValue, searchFields]);

  return {
    searchValue,
    setSearchValue,
    filteredData,
    handleSearchChange,
    isSearching: searchValue !== debouncedSearchValue,
  };
};

export default useDataGridSearchDebounced;
